import {
  IContractTemplateFlattenedTreeForm,
  TEMPLATE_TYPE,
} from 'services/templates/types';

export const isParagraphSectionType = (type: TEMPLATE_TYPE) =>
  type === TEMPLATE_TYPE.paragraphSection;

export const getParagraphSectionRoot = (
  items: IContractTemplateFlattenedTreeForm[],
  itemId: string
): IContractTemplateFlattenedTreeForm | undefined => {
  let current = items.find(item => item.id === itemId);

  while (current) {
    if (isParagraphSectionType(current.type)) {
      return current;
    }

    if (!current.parentId) {
      return undefined;
    }

    current = items.find(item => item.id === current!.parentId);
  }

  return undefined;
};

export const isInsideParagraphMode = (
  items: IContractTemplateFlattenedTreeForm[],
  itemId: string
): boolean => !!getParagraphSectionRoot(items, itemId);

const getNumberedSiblings = (
  items: IContractTemplateFlattenedTreeForm[],
  parentId: string
) =>
  items.filter(
    item =>
      item.parentId === parentId &&
      (item.type === TEMPLATE_TYPE.section ||
        item.type === TEMPLATE_TYPE.agreement)
  );

export const getParagraphSectionNumber = (
  items: IContractTemplateFlattenedTreeForm[],
  paragraphSectionId: string
): number => {
  const contractChildren = items.filter(
    item => item.parentId === '0' && isParagraphSectionType(item.type)
  );

  return contractChildren.findIndex(item => item.id === paragraphSectionId) + 1;
};

export const getParagraphChildPath = (
  items: IContractTemplateFlattenedTreeForm[],
  itemId: string
): number[] => {
  const root = getParagraphSectionRoot(items, itemId);
  if (!root || root.id === itemId) {
    return [];
  }

  const path: number[] = [];
  let currentId: string | null = itemId;

  while (currentId && currentId !== root.id) {
    const current = items.find(item => item.id === currentId);
    if (!current?.parentId) {
      break;
    }

    const parent = items.find(item => item.id === current.parentId);
    if (!parent) {
      break;
    }

    const siblings = getNumberedSiblings(items, parent.id);
    const index = siblings.findIndex(sibling => sibling.id === currentId);
    if (index < 0) {
      break;
    }

    path.unshift(index);
    currentId = parent.id;
  }

  return path;
};

export const formatParagraphNumberLabel = (
  items: IContractTemplateFlattenedTreeForm[],
  itemId: string
): string | undefined => {
  const item = items.find(treeItem => treeItem.id === itemId);
  if (!item) {
    return undefined;
  }

  if (isParagraphSectionType(item.type)) {
    const number = getParagraphSectionNumber(items, item.id);
    return number > 0 ? `§${number}.` : undefined;
  }

  if (!isInsideParagraphMode(items, itemId)) {
    return undefined;
  }

  const path = getParagraphChildPath(items, itemId);
  if (!path.length) {
    return undefined;
  }

  return `${path.map(index => index + 1).join('.')}.`;
};

const getTargetParagraphRoot = (
  items: IContractTemplateFlattenedTreeForm[],
  targetParentId: string | null
): IContractTemplateFlattenedTreeForm | undefined => {
  if (!targetParentId) {
    return undefined;
  }

  const targetParent = items.find(item => item.id === targetParentId);
  if (targetParent && isParagraphSectionType(targetParent.type)) {
    return targetParent;
  }

  return getParagraphSectionRoot(items, targetParentId);
};

export const canMoveInSameParagraphContext = (
  items: IContractTemplateFlattenedTreeForm[],
  activeItem: IContractTemplateFlattenedTreeForm,
  targetParentId: string | null
): boolean => {
  const activeInParagraph = isInsideParagraphMode(items, activeItem.id);
  const activeIsParagraphSection = isParagraphSectionType(activeItem.type);
  const targetParagraphRoot = getTargetParagraphRoot(items, targetParentId);

  if (activeIsParagraphSection) {
    return targetParentId === '0';
  }

  if (activeInParagraph) {
    const activeRoot = getParagraphSectionRoot(items, activeItem.id);
    return !!activeRoot && activeRoot.id === targetParagraphRoot?.id;
  }

  return !targetParagraphRoot;
};

export const canAddNestedTextSectionInParagraphMode = (
  items: IContractTemplateFlattenedTreeForm[],
  parentId: string
): boolean => {
  const parent = items.find(item => item.id === parentId);
  if (!parent) {
    return false;
  }

  if (isParagraphSectionType(parent.type)) {
    return true;
  }

  if (
    parent.type === TEMPLATE_TYPE.section &&
    isInsideParagraphMode(items, parentId)
  ) {
    return false;
  }

  return true;
};

export const canNestSectionUnderParentInParagraphMode = (
  items: IContractTemplateFlattenedTreeForm[],
  targetParentId: string | null
): boolean => {
  if (!targetParentId) {
    return true;
  }

  return canAddNestedTextSectionInParagraphMode(items, targetParentId);
};
